import { notesInterface } from 'Interfaces/FDPInterfaces/tmsInterfaces';
import {
  bulkEditDataInterface,
  filterInterface,
} from 'Interfaces/FDPInterfaces/constrainedForecastBulkEdit';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { constrainedForecastService } from 'services/fdp/constrainedForecastService';
import { unconstrainedForecastService } from 'services/fdp/unconstrainedForecastService';
import { convertIsoToDateString } from 'utils/dateUtilities';
import { deepCopy } from 'utils/deepCopy';

// Define forecast type for the new system
type ForecastType = 'forecast' | 'old' | 'saved';

export const useConstrainedForecastBulkEdit = () => {
  const [editData, setEditData] = useState<bulkEditDataInterface[] | []>([]);
  const [editDataCopy, setEditDataCopy] = useState<bulkEditDataInterface[] | []>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [filterpayload, setFilterpayload] = useState<filterInterface>({
    Ramping: '',
    Concept: '',
    MoleculeAndDosageForm: '',
    StrategyCode: '',
    SellingCategory: '',
    NDC11: '',
    ConceptSort: '', // Add ConceptSort for sorting functionality
  });
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [ndcNotesSet, setNdcNotesSet] = useState<Set<string>>(new Set());
  const [ndcEditedSet, setNdcEditedSet] = useState<Set<string>>(new Set());
  const [ndcError, setNdcError] = useState<Set<string>>(new Set());
  const [ndcSaveLoading, setNdcSaveLoading] = useState<Map<string, boolean>>(new Map());
  const [isSaveAllLoading, setIsSaveloading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [initialloading, setInitialLoading] = useState<boolean>(true);
  const [nextPageLoading, setNextPageLoading] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Stays false until the initial page-1 fetch finishes. Prevents loadMore from
  // firing a page-2 call before the initial load has completed.
  const initialLoadDoneRef = useRef<boolean>(false);

  // New states for accordion and type selection
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [forecastTypeSelection, setForecastTypeSelection] = useState<Map<string, ForecastType>>(
    new Map()
  );

  const [singleSaveModalVisibility, setSingleSaveModalVisibility] = useState<boolean>(false);
  const [saveAllModalVisibility, setSaveAllModalVisibility] = useState<boolean>(false);

  //   states for notes
  const [isNotesLoading, setIsNotesLoading] = useState<boolean>(false);
  const [notesData, setNotesData] = useState<notesInterface[]>();
  const [isNoteSaving, setIsNoteSaving] = useState<boolean>(false);
  const [_isNoteEditing, setIsNoteEditing] = useState<boolean>(false);
  const [isNoteEdited, setIsNoteEdited] = useState<Map<number, boolean>>(new Map());
  const [isNoteDeleted, setIsNoteDeleted] = useState<Map<number, boolean>>(new Map());
  const [deletingNote, setDeletingNote] = useState<notesInterface | null>(null);
  const [isNoteDeleting, setIsNoteDeleting] = useState<boolean>(false);
  const [isDeleteClicked, setIsDeleteClicked] = useState<boolean>(false);
  const [latestNote, _setLatestNote] = useState<notesInterface>();
  const [focusedRowForNoteAdd, setFocusedRowForNoteAdd] = useState<number | null>(null);

  // alert states
  const [showCancelAlert, setShowCancelAlert] = useState<boolean>(false);
  const [showFilterAlert, setShowFilterAlert] = useState<boolean>(false);
  const navigate = useNavigate();

  // Helper function to get NDC key for type selection
  const getNDCKey = (rowIndex: number): string => {
    const rowData = editData[rowIndex];
    return rowData.NDC11;
  };

  // Get available forecast types for a row
  const getAvailableForecastTypes = (rowIndex: number): ForecastType[] => {
    const rowData = editData[rowIndex];
    const availableTypes: ForecastType[] = ['forecast']; // Forecast is always available

    // Check if saved values exist
    if (rowData.saved_values) {
      availableTypes.push('saved');
    }

    // Check if old values exist
    if (rowData.old_values_present) {
      availableTypes.push('old');
    }

    return availableTypes;
  };

  // Get default forecast type for a row
  const getDefaultForecastType = (rowIndex: number): ForecastType => {
    const rowData = editData[rowIndex];

    // Priority: saved > old > forecast
    if (rowData.saved_values) {
      return 'saved';
    }

    if (rowData.old_values_present) {
      return 'old';
    }

    return 'forecast';
  };

  // Check if forecast type is changeable for a row
  const isForecastTypeChangeable = (rowIndex: number): boolean => {
    const rowData = editData[rowIndex];

    // If saved values exist, not changeable
    if (rowData.saved_values) {
      return false;
    }

    // If only forecast values exist, not changeable
    if (!rowData.old_values_present) {
      return false;
    }

    // If old values exist but no saved values, changeable between old and forecast
    return true;
  };

  // Initialize forecast type for a row
  const initializeForecastTypeForRow = (rowIndex: number) => {
    const ndcKey = getNDCKey(rowIndex);

    // Only set if not already set
    if (!forecastTypeSelection.has(ndcKey)) {
      const defaultType = getDefaultForecastType(rowIndex);
      setForecastTypeSelection(prev => {
        const newMap = new Map(prev);
        newMap.set(ndcKey, defaultType);
        return newMap;
      });
    }
  };

  // Get selected forecast type for a row
  const getSelectedForecastType = (rowIndex: number): ForecastType => {
    const ndcKey = getNDCKey(rowIndex);

    // Initialize if not set
    if (!forecastTypeSelection.has(ndcKey)) {
      const defaultType = getDefaultForecastType(rowIndex);
      setForecastTypeSelection(prev => {
        const newMap = new Map(prev);
        newMap.set(ndcKey, defaultType);
        return newMap;
      });
      return defaultType;
    }

    return forecastTypeSelection.get(ndcKey) || getDefaultForecastType(rowIndex);
  };

  // Handle forecast type change
  const handleForecastTypeChange = (rowIndex: number, type: ForecastType) => {
    const ndcKey = getNDCKey(rowIndex);
    setForecastTypeSelection(prev => {
      const newMap = new Map(prev);
      newMap.set(ndcKey, type);
      return newMap;
    });
  };

  // Check if should show toggle for forecast type
  const shouldShowToggle = (rowIndex: number): boolean => {
    return isForecastTypeChangeable(rowIndex);
  };

  // Accordion functionality
  const toggleRowExpansion = (rowIndex: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex);
      } else {
        newSet.add(rowIndex);
      }
      return newSet;
    });
  };

  const isRowExpanded = (rowIndex: number): boolean => {
    return expandedRows.has(rowIndex);
  };

  // Get baseline value for accordion display (UnconstrainedForecast for constrained)
  const getBaselineValue = (rowIndex: number, forecastPeriod: string): string => {
    const data = editData[rowIndex].forecast_data;
    for (let i = 0; i < data.length; i++) {
      if (data[i].ForecastPeriod === forecastPeriod) {
        return data[i].UnconstrainedForecast.toString();
      }
    }
    return '-';
  };

  // Computes the Model/Manual status for a raw data row using the same logic as getRowStatus.
  // Used to enforce StrategyCode filter consistency client-side so the Status column
  // always matches the selected filter, regardless of what the backend's stratCode field says.
  const computeRowStatus = (rowData: bulkEditDataInterface): 'model' | 'manual' => {
    const defaultType = getDefaultForecastTypeFromData(rowData);
    for (const monthData of rowData.forecast_data) {
      let currentValue = 0;
      switch (defaultType) {
        case 'forecast':
          currentValue = monthData.ConstrainedForecast;
          break;
        case 'old':
          currentValue = monthData.OldConstrainedForecast;
          break;
        case 'saved':
          currentValue = monthData.savedConstraintForecast;
          break;
        default:
          currentValue = monthData.ConstrainedForecast;
      }
      if (Math.abs(currentValue - monthData.UnconstrainedForecast) > 0.001) {
        return 'manual';
      }
    }
    return 'model';
  };

  const applyStrategyCodeFilter = (
    rows: bulkEditDataInterface[],
    strategyCode: string
  ): bulkEditDataInterface[] => {
    if (!strategyCode) return rows;
    return rows.filter(row => computeRowStatus(row) === strategyCode.toLowerCase());
  };

  // Get row status - Model if all values match UnconstrainedForecast, Manual if any differ
  const getRowStatus = (rowIndex: number): 'Model' | 'Manual' => {
    const rowData = editData[rowIndex];
    const selectedType = getSelectedForecastType(rowIndex);

    for (let i = 0; i < rowData.forecast_data.length; i++) {
      const monthData = rowData.forecast_data[i];
      let currentValue = 0;

      // Get current value based on selected type
      switch (selectedType) {
        case 'forecast':
          currentValue = monthData.ConstrainedForecast;
          break;
        case 'old':
          currentValue = monthData.OldConstrainedForecast;
          break;
        case 'saved':
          currentValue = monthData.savedConstraintForecast;
          break;
        default:
          currentValue = monthData.ConstrainedForecast;
      }

      // If any month differs from UnconstrainedForecast, it's Manual
      if (Math.abs(currentValue - monthData.UnconstrainedForecast) > 0.001) {
        return 'Manual';
      }
    }

    // If all months match UnconstrainedForecast, it's Model
    return 'Model';
  };

  // Concept sorting functionality
  const handleConceptSort = (sortType: 'asc' | 'desc' | '') => {
    // Clear all existing data
    setEditData([]);
    setEditDataCopy([]);
    setNdcSaveLoading(new Map());
    setNdcEditedSet(new Set());
    setNdcNotesSet(new Set());
    setExpandedRows(new Set());
    setNdcError(new Set());
    setForecastTypeSelection(new Map());

    // Update filter data with new sort
    const newFilterData = {
      ...filterpayload,
      ConceptSort: sortType,
    };
    setFilterpayload(newFilterData);
    setHasMore(true);
    setPageNumber(1);
    initialLoadDoneRef.current = false;

    // Fetch data with new sort immediately
    fetchDataWithSort(newFilterData);
  };

  const fetchDataWithSort = async (filterDataWithSort: filterInterface) => {
    try {
      setIsDataLoading(true);
      setInitialLoading(true);
      const payload = {
        page_no: 1,
        filters: filterDataWithSort,
      };

      const data = await constrainedForecastService.getFilteredData(payload);
      setPageNumber(2);

      if (data.length === 0) {
        setHasMore(false);
        return;
      }

      if (data) {
        setEditData(data);
        setEditDataCopy(deepCopy(data));

        // Build forecast type map directly from fresh data.
        // Do NOT call initializeForecastTypeForRow here — it reads the editData
        // state which is still stale (React batches state updates).
        const newTypes = new Map<string, ForecastType>();
        data.forEach(rowData => {
          if (!newTypes.has(rowData.NDC11)) {
            newTypes.set(rowData.NDC11, getDefaultForecastTypeFromData(rowData));
          }
        });
        setForecastTypeSelection(newTypes);
      }
    } catch (_error) {
      console.log('Error while getting the data');
    } finally {
      setIsDataLoading(false);
      setInitialLoading(false);
      initialLoadDoneRef.current = true;
    }
  };

  const getNotesData = async (ndc: string) => {
    try {
      setIsNotesLoading(true);
      const data = await unconstrainedForecastService.getNDC11Note(ndc, 'constrained');
      if (data) {
        setNotesData(data);
      }
    } catch (_error) {
      console.log('Error while getting notes data');
    } finally {
      setIsNotesLoading(false);
    }
  };

  const handleAddNote = async (ndc: string, value: string) => {
    let data: notesInterface[] = [];
    try {
      setIsNoteSaving(true);
      if (value?.trim() === '') {
        toast.warning('Cannot save an empty note!!');
      } else {
        const payload = {
          ndc11: ndc,
          note: value,
          planning_type: 'constrained',
        };
        data = await unconstrainedForecastService.saveNDC11Note(payload);
        ndcNotesSet.add(ndc);

        setNdcError(prev => {
          const temp = new Set(prev);
          temp.delete(ndc);
          return temp;
        });
      }
      return true;
    } catch (_error) {
      console.log('Error saving the note');
      toast.error('Something went wrong!!');
    } finally {
      setNotesData(data);
      setIsNoteSaving(false);
    }
    return false;
  };

  const handleEditNote = async (id: number, note: string) => {
    try {
      setIsNoteEditing(true);
      const data = await unconstrainedForecastService.updateNote(id, note);
      if (data.status && notesData) {
        notesData.map(item => {
          if (item.ID === id) {
            item.CreatedDate = convertIsoToDateString(data.lastUpdatedDate) || '';
          }
        });
        toast.success('Note Updated successfully.');
      }
    } catch (_error) {
      toast.error('Something went wrong.');
    } finally {
      setIsNoteEditing(false);
    }
  };

  const handleDeleteNote = async (id: number) => {
    if (focusedRowForNoteAdd === null) return;

    try {
      setIsNoteDeleting(true);
      const data = await unconstrainedForecastService.deleteNote(id);
      if (data) {
        toast.success('Note deleted successfully.');
        if (notesData) {
          // removing the deleted note from the state in the application after successful deletion.
          const notes = notesData.filter(note => {
            if (note.ID !== id) {
              return note;
            }
          });

          setNotesData([...notes]);
        }
      }
    } catch (_error) {
      console.log('handleEditNote=> ', error);
      toast.error('Something went wrong.');
    } finally {
      setIsNoteDeleting(false);
      setIsDeleteClicked(false);
    }
  };

  // Updated getForecastValue to work with new data structure and type selection
  const getForecastValue = (ndc: string, period: string) => {
    const ndcData = editData.filter(item => item.NDC11 === ndc);
    if (!ndcData[0]) return '';

    const rowIndex = editData.findIndex(item => item.NDC11 === ndc);
    const selectedType = getSelectedForecastType(rowIndex);

    const value = ndcData[0].forecast_data.filter(item => item.ForecastPeriod === period);
    if (value && value[0]) {
      const fmtNum = (v: number) => Number(v).toLocaleString('en-US');
      switch (selectedType) {
        case 'forecast':
          return fmtNum(value[0].ConstrainedForecast);
        case 'old':
          return fmtNum(value[0].OldConstrainedForecast);
        case 'saved':
          return fmtNum(value[0].savedConstraintForecast);
        default:
          return fmtNum(value[0].ConstrainedForecast);
      }
    }
    return '';
  };

  const isNumeric = (value: string) => {
    return /^[0-9]{1,9}$/.test(value);
  };

  // Updated setForecastValue to work with new data structure and type selection
  const setForecastValue = (idx: number, period: string, adjustment: string) => {
    adjustment = adjustment.replace(/,/g, '').replace(/^0+/, '') || '0';
    if (!isNumeric(adjustment)) return;

    setFocusedRowForNoteAdd(idx);
    const numericAdjustment = Number(adjustment);
    const tempData = deepCopy(editData);
    const ndcData = tempData[idx];
    const selectedType = getSelectedForecastType(idx);

    // Update the forecast value based on selected type
    const value = ndcData.forecast_data.find(item => item.ForecastPeriod === period);
    if (value) {
      switch (selectedType) {
        case 'forecast':
          value.ConstrainedForecast = numericAdjustment;
          break;
        case 'old':
          value.OldConstrainedForecast = numericAdjustment;
          break;
        case 'saved':
          value.savedConstraintForecast = numericAdjustment;
          break;
      }
    }

    setEditData(tempData);

    // Check if the data has changed and update edited set
    const hasChanged = hasConstrainedForecastDifferences(
      editDataCopy[idx].forecast_data,
      tempData[idx].forecast_data
    );
    const tempSet = ndcEditedSet;
    if (hasChanged) {
      tempSet.add(ndcData.NDC11);
    } else {
      tempSet.delete(ndcData.NDC11);
    }
    setNdcEditedSet(tempSet);
  };

  // Updated comparison function for new data structure
  function hasConstrainedForecastDifferences(
    a: {
      ForecastPeriod: string;
      UnconstrainedForecastID: number;
      UnconstrainedForecast: number;
      ConstrainedForecast: number;
      Adjustment: number;
      percentadj: number;
      OldConstrainedForecast: number;
      oldPercentAdj: number;
      savedConstraintForecast: number;
      savedContraintForecastPerc: number;
      SellingCategory: string;
    }[],
    b: {
      ForecastPeriod: string;
      UnconstrainedForecastID: number;
      UnconstrainedForecast: number;
      ConstrainedForecast: number;
      Adjustment: number;
      percentadj: number;
      OldConstrainedForecast: number;
      oldPercentAdj: number;
      savedConstraintForecast: number;
      savedContraintForecastPerc: number;
      SellingCategory: string;
    }[]
  ) {
    if (a.length !== b.length) {
      return true;
    }

    for (let i = 0; i < a.length; i++) {
      if (
        a[i].ConstrainedForecast != b[i].ConstrainedForecast ||
        a[i].OldConstrainedForecast != b[i].OldConstrainedForecast ||
        a[i].savedConstraintForecast != b[i].savedConstraintForecast
      ) {
        return true;
      }
    }

    return false;
  }

  const isSalveAllBtnEnabled = () => {
    // Check if any row has save button enabled
    for (let i = 0; i < editData.length; i++) {
      if (isBtnEnabled(i)) {
        return true;
      }
    }
    return false;
  };

  // Save button is enabled only when the row has unsaved changes vs the initial loaded data.
  const isBtnEnabled = (idx: number) => {
    const initialRowData = editDataCopy[idx];
    if (!initialRowData) return false;
    return hasConstrainedForecastDifferences(
      initialRowData.forecast_data,
      editData[idx].forecast_data
    );
  };

  const getCurrentDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
  };

  const handleSingleSave = async (idx: number) => {
    if (focusedRowForNoteAdd === null) return;

    const ndcData = editData[idx];
    const selectedType = getSelectedForecastType(idx);
    const ndcKey = ndcData.NDC11;

    // Clear any previous errors and proceed with save
    setNdcError(prevNdcError => {
      const newSet = new Set(prevNdcError);
      newSet.delete(ndcKey);
      return newSet;
    });

    // Create payload with new data structure
    const payload: {
      NDC11: string;
      PlanningPeriod: string;
      ForecastPeriod: string;
      UnconstrainedForecast: number;
      ConstrainedForecast: number;
      Adjustment: number;
      Notes: string;
      SellingCategory: string;
    }[] = [];
    const planning_period = getCurrentDate();

    for (const item of ndcData.forecast_data) {
      // Get the value based on selected type
      let forecastValue = 0;
      switch (selectedType) {
        case 'forecast':
          forecastValue = item.ConstrainedForecast;
          break;
        case 'old':
          forecastValue = item.OldConstrainedForecast;
          break;
        case 'saved':
          forecastValue = item.savedConstraintForecast;
          break;
        default:
          forecastValue = item.ConstrainedForecast;
      }

      const obj = {
        NDC11: ndcData.NDC11,
        PlanningPeriod: planning_period,
        ForecastPeriod: item.ForecastPeriod,
        UnconstrainedForecast: Math.round(Number(item.UnconstrainedForecastID)),
        ConstrainedForecast: Math.round(Number(forecastValue)),
        Adjustment: Math.round(Number(forecastValue) - Number(item.UnconstrainedForecast)),
        Notes: 'notes',
        SellingCategory: ndcData.SellingCategory,
      };
      payload.push(obj);
    }

    try {
      setNdcSaveLoading(prev => {
        const newMap = new Map(prev);
        newMap.set(ndcData.NDC11, true);
        return newMap;
      });
      const data = await constrainedForecastService.saveConstrainedForecast(payload);
      if (data) {
        setFocusedRowForNoteAdd(null);
        toast.success('Constrained forecast data is saved successfully.');
        markTheRowsSaved([idx]);
        setSingleSaveModalVisibility(prev => !prev);
        setNdcEditedSet(prev => {
          const temp = new Set(prev);
          temp.delete(ndcData.NDC11);
          return temp;
        });
      }
    } catch (_error) {
      console.log(`Error while saving the Constrained forecast for ndc - ${ndcData.NDC11}`, error);
      toast.error('Something went wrong while saving Constrained forecast');
    } finally {
      ndcNotesSet.delete(`${ndcData.NDC11}-${latestNote?.CreatedDate}`);
      setNdcSaveLoading(prev => {
        const newMap = new Map(prev);
        newMap.set(ndcData.NDC11, false);
        return newMap;
      });
    }
  };

  const markTheRowsSaved = (rowIndex: number[]) => {
    try {
      const tempEditData = deepCopy(editData);
      const tempSelectedType = new Map(forecastTypeSelection);
      const tempEditDataCopy = deepCopy(editDataCopy);
      rowIndex.forEach(index => {
        const data = tempEditData[index].forecast_data;
        const ndcKey = getNDCKey(index);
        const initialData = tempEditDataCopy[index].forecast_data;
        const selectedType = forecastTypeSelection.get(ndcKey) || 'forecast';

        tempSelectedType.set(ndcKey, 'saved');
        tempEditData[index].saved_values = true;
        tempEditDataCopy[index].saved_values = true;

        data.forEach((element, idx) => {
          let constraintValue: number = 0;
          // Get the value based on selected forecast type
          switch (selectedType) {
            case 'forecast':
              constraintValue = Number(element.ConstrainedForecast);
              element.savedConstraintForecast = constraintValue;
              initialData[idx].savedConstraintForecast = constraintValue;
              break;
            case 'saved':
              constraintValue = Number(element.savedConstraintForecast);
              element.savedConstraintForecast = constraintValue;
              initialData[idx].savedConstraintForecast = constraintValue;
              break;
            case 'old':
              constraintValue = Number(element.OldConstrainedForecast);
              element.savedConstraintForecast = constraintValue;
              initialData[idx].savedConstraintForecast = constraintValue;
              break;
            default:
              constraintValue = Number(element.ConstrainedForecast);
              element.savedConstraintForecast = constraintValue;
              initialData[idx].savedConstraintForecast = constraintValue;
          }
        });

        // Sync all forecast_data fields from editData into editDataCopy so that
        // hasConstrainedForecastDifferences returns false and the Save button is disabled.
        tempEditDataCopy[index].forecast_data = deepCopy(tempEditData[index].forecast_data);
      });

      setEditData(tempEditData);
      setEditDataCopy(tempEditDataCopy);
      setForecastTypeSelection(tempSelectedType);
    } catch (_error) {
      console.log('Error while marking the rows saved', error);
    }
  };

  const handleSaveAllEdited = async () => {
    // Only save rows that have changes compared to initial loaded data.
    const rowsToSave: number[] = [];

    for (let i = 0; i < editData.length; i++) {
      if (isBtnEnabled(i)) {
        rowsToSave.push(i);
      }
    }

    setNdcError(new Set());
    setIsSaveloading(true);

    const payload: {
      NDC11: string;
      PlanningPeriod: string;
      ForecastPeriod: string;
      UnconstrainedForecast: number;
      ConstrainedForecast: number;
      Adjustment: number;
      Notes: string;
      SellingCategory: string;
    }[] = [];

    const planning_period = getCurrentDate();

    for (const rowIndex of rowsToSave) {
      const data = editData[rowIndex];
      const selectedType = getSelectedForecastType(rowIndex);

      data.forecast_data.forEach(ele => {
        // Get the value based on selected type
        let forecastValue = 0;
        switch (selectedType) {
          case 'forecast':
            forecastValue = ele.ConstrainedForecast;
            break;
          case 'old':
            forecastValue = ele.OldConstrainedForecast;
            break;
          case 'saved':
            forecastValue = ele.savedConstraintForecast;
            break;
          default:
            forecastValue = ele.ConstrainedForecast;
        }

        const obj = {
          NDC11: data.NDC11,
          PlanningPeriod: planning_period,
          ForecastPeriod: ele.ForecastPeriod,
          UnconstrainedForecast: Math.round(Number(ele.UnconstrainedForecastID)),
          ConstrainedForecast: Math.round(Number(forecastValue)),
          Adjustment: Math.round(Number(forecastValue) - Number(ele.UnconstrainedForecast)),
          Notes: 'notes',
          SellingCategory: data.SellingCategory,
        };
        payload.push(obj);
      });
    }

    try {
      if (payload.length > 0) {
        setIsSaveloading(true);
        const data = await constrainedForecastService.saveConstrainedForecast(payload);
        if (data) {
          toast.success(
            'Successfully saved Constrained forecast data for all the highlighted rows.'
          );
          markTheRowsSaved(rowsToSave);
          setSaveAllModalVisibility(prev => !prev);
          setNdcNotesSet(new Set());
        }
      }
    } catch (_error) {
      console.log('handleSaveAllEdited=> ', error);
      toast.error('Something went wrong while saving the constrained forecast.');
    } finally {
      setIsSaveloading(false);
      setNdcEditedSet(new Set());
      setNdcNotesSet(new Set());
    }
  };

  const getNextPageData = async () => {
    try {
      // Cancel any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create a new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setNextPageLoading(true);
      const payload = {
        page_no: pageNumber,
        filters: filterpayload,
      };

      // Pass the signal to the fetch request
      const data = await constrainedForecastService.getFilteredData(payload);

      if (data) {
        if (data.length === 0) {
          setHasMore(false);
          return;
        }
        // Apply the same client-side StrategyCode filter used in fetchDataWithFilter so
        // pagination results are always consistent with what the Status column displays.
        const filteredNewData = applyStrategyCodeFilter(data, filterpayload.StrategyCode);
        if (filteredNewData.length === 0 && data.length > 0) {
          // All page results filtered out client-side — no more matching data.
          setHasMore(false);
          return;
        }
        const currentLength = editData.length;
        const newEditData = [...editData, ...filteredNewData];
        const newEditDataCopy = [...editDataCopy, ...deepCopy(filteredNewData)];

        // Update the state first
        setEditData(newEditData);
        setEditDataCopy(newEditDataCopy);
        setPageNumber(prev => prev + 1);

        // Then initialize forecast types for newly added rows using the updated data
        filteredNewData.forEach((_, index) => {
          const actualRowIndex = currentLength + index;
          const ndcKey = newEditData[actualRowIndex].NDC11;
          const defaultType = getDefaultForecastTypeFromData(newEditData[actualRowIndex]);

          setForecastTypeSelection(prev => {
            const newMap = new Map(prev);
            if (!newMap.has(ndcKey)) {
              newMap.set(ndcKey, defaultType);
            }
            return newMap;
          });
        });
      } else {
        setHasMore(false);
      }
    } catch (error: any) {
      // Don't show error if it was caused by an abort
      if (error.name !== 'AbortError') {
        console.log('Error while getting the data', error);
        toast.error('Something went wrong while fetching the data');
      }
    } finally {
      // Only clear loading state if this is the current request (not aborted)
      if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        setNextPageLoading(false);
        setInitialLoading(false);
      }
    }
  };

  // Helper function to get default forecast type from row data directly
  const getDefaultForecastTypeFromData = (rowData: bulkEditDataInterface): ForecastType => {
    // Priority: saved > old > forecast
    if (rowData.saved_values) {
      return 'saved';
    }

    if (rowData.old_values_present) {
      return 'old';
    }

    return 'forecast';
  };

  const loadMore = async () => {
    if (nextPageLoading || !hasMore || !initialLoadDoneRef.current) return;
    setNextPageLoading(true);
    getNextPageData();
  };

  const isRowsEdited = () => {
    for (let i = 0; i < editData.length; i++) {
      if (
        hasConstrainedForecastDifferences(editDataCopy[i].forecast_data, editData[i].forecast_data)
      ) {
        return true;
      }
    }
    return false;
  };

  const fetchDataWithFilter = async (filterData: filterInterface) => {
    try {
      setIsDataLoading(true);
      setInitialLoading(true);
      const data = await constrainedForecastService.getFilteredData({
        page_no: 1,
        filters: filterData,
      });
      setPageNumber(2);
      if (!data || data.length === 0) {
        setHasMore(false);
        return;
      }
      const filteredData = applyStrategyCodeFilter(data, filterData.StrategyCode);
      if (filteredData.length === 0) {
        setHasMore(false);
        return;
      }
      setEditData(filteredData);
      setEditDataCopy(deepCopy(filteredData));
      const newTypes = new Map<string, ForecastType>();
      filteredData.forEach(rowData => {
        if (!newTypes.has(rowData.NDC11)) {
          newTypes.set(rowData.NDC11, getDefaultForecastTypeFromData(rowData));
        }
      });
      setForecastTypeSelection(newTypes);
    } catch (_error) {
      toast.error('Something went wrong while fetching filtered data');
    } finally {
      setIsDataLoading(false);
      setInitialLoading(false);
      initialLoadDoneRef.current = true;
    }
  };

  const handleApplyFilter = (newFilterData: filterInterface) => {
    // Abort any in-flight pagination to prevent stale unfiltered data overwriting filtered results
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setNextPageLoading(false);

    setEditData([]);
    setEditDataCopy([]);
    setPageNumber(1);
    setIsDataLoading(false);
    setNdcNotesSet(new Set());
    setNdcError(new Set());
    setNdcSaveLoading(new Map());
    setIsSaveloading(false);
    setIsNotesLoading(false);
    setNotesData([]);
    setIsNoteSaving(false);
    setIsNoteEdited(new Map());
    setIsNoteDeleted(new Map());
    setHasMore(true);
    setNdcEditedSet(new Set());
    setShowCancelAlert(false);
    setShowFilterAlert(false);
    setSingleSaveModalVisibility(false);
    setSaveAllModalVisibility(false);
    setIsDeleteClicked(false);
    setDeletingNote(null);
    setInitialLoading(true);
    setExpandedRows(new Set());
    setForecastTypeSelection(new Map());
    setFilterpayload(newFilterData);
    initialLoadDoneRef.current = false;
    fetchDataWithFilter(newFilterData);
  };

  const handleCopy = (idx: number, month: string) => {
    const tempData = deepCopy(editData);
    const selectedType = getSelectedForecastType(idx);

    // Get the value based on selected type
    let sourceValue: number;
    const monthData = editData[idx].forecast_data.filter(item => item.ForecastPeriod === month)[0];

    switch (selectedType) {
      case 'forecast':
        sourceValue = monthData.ConstrainedForecast;
        break;
      case 'old':
        sourceValue = monthData.OldConstrainedForecast;
        break;
      case 'saved':
        sourceValue = monthData.savedConstraintForecast;
        break;
      default:
        sourceValue = monthData.ConstrainedForecast;
    }

    const forecastDate = new Date(month);

    // copying all the data in cells
    for (const item of tempData[idx].forecast_data) {
      if (new Date(item.ForecastPeriod) > forecastDate) {
        // Update the appropriate field based on selected type
        switch (selectedType) {
          case 'forecast':
            item.ConstrainedForecast = Number(sourceValue) || 0;
            break;
          case 'old':
            item.OldConstrainedForecast = Number(sourceValue) || 0;
            break;
          case 'saved':
            item.savedConstraintForecast = Number(sourceValue) || 0;
            break;
          default:
            item.ConstrainedForecast = Number(sourceValue) || 0;
        }
      }
    }

    // Check if the data is updated
    const hasChanged = hasConstrainedForecastDifferences(
      editDataCopy[idx].forecast_data,
      tempData[idx].forecast_data
    );
    const tempSet = ndcEditedSet;
    if (hasChanged) {
      tempSet.add(editData[idx].NDC11);
    } else {
      tempSet.delete(editData[idx].NDC11);
    }
    setNdcEditedSet(tempSet);

    setEditData(tempData);
  };

  const handleCancelButtonClick = () => {
    if (isRowsEdited()) {
      setShowCancelAlert(true);
    } else {
      navigate(-1);
    }
  };

  // Initial data loading function
  const getInitialData = async () => {
    try {
      setIsDataLoading(true);
      setInitialLoading(true);
      const payload = {
        page_no: 1,
        filters: filterpayload,
      };

      const data = await constrainedForecastService.getFilteredData(payload);
      setPageNumber(2);

      if (data.length === 0) {
        setHasMore(false);
        return;
      }

      if (data) {
        setEditData(data);
        setEditDataCopy(deepCopy(data));

        // Build forecast type map directly from fresh data.
        // Do NOT call initializeForecastTypeForRow here — it reads the editData
        // state which is still stale (React batches state updates).
        const newTypes = new Map<string, ForecastType>();
        data.forEach(rowData => {
          if (!newTypes.has(rowData.NDC11)) {
            newTypes.set(rowData.NDC11, getDefaultForecastTypeFromData(rowData));
          }
        });
        setForecastTypeSelection(newTypes);
      }
    } catch (_error) {
      console.log('Error while getting the initial data');
      toast.error('Something went wrong while fetching initial data');
    } finally {
      setIsDataLoading(false);
      setInitialLoading(false);
      initialLoadDoneRef.current = true;
    }
  };

  // Load initial data on component mount
  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    editData,
    setEditData,
    setEditDataCopy,
    pageNumber,
    setPageNumber,
    filterpayload,
    setFilterpayload,
    isDataLoading,
    setIsDataLoading,
    getForecastValue,
    setForecastValue,
    isBtnEnabled,
    isSalveAllBtnEnabled,
    ndcNotesSet,
    setNdcNotesSet,
    ndcError,
    setNdcError,
    handleSingleSave,
    handleSaveAllEdited,
    ndcSaveLoading,
    isSaveAllLoading,
    isNotesLoading,
    notesData,
    setNotesData,
    isNoteSaving,
    getNotesData,
    handleAddNote,
    hasMore,
    nextPageLoading,
    loadMore,
    showCancelAlert,
    setShowCancelAlert,
    showFilterAlert,
    setShowFilterAlert,
    isRowsEdited,
    singleSaveModalVisibility,
    setSingleSaveModalVisibility,
    saveAllModalVisibility,
    setSaveAllModalVisibility,
    isNoteEdited,
    setIsNoteEdited,
    isNoteDeleted,
    setIsNoteDeleted,
    handleCopy,
    handleEditNote,
    handleDeleteNote,
    deletingNote,
    isNoteDeleting,
    isDeleteClicked,
    setIsDeleteClicked,
    setDeletingNote,
    initialloading,
    handleApplyFilter,
    handleCancelButtonClick,
    // New exports for accordion functionality
    expandedRows,
    toggleRowExpansion,
    isRowExpanded,
    getBaselineValue,
    // New exports for type selection functionality
    forecastTypeSelection,
    getSelectedForecastType,
    handleForecastTypeChange,
    shouldShowToggle,
    getAvailableForecastTypes,
    getDefaultForecastType,
    isForecastTypeChangeable,
    initializeForecastTypeForRow,
    // New export for concept sorting
    handleConceptSort,
    getRowStatus,
    markTheRowsSaved,
  };
};
